import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Logo } from '../components/shell/Logo'
import { Input } from '../components/ui'
import { useAuth } from '../contexts/AuthContext'
import { useReturnDestination, withReturnPath } from '../lib/returnPath'
import { isPublicOnboardingEnabled } from '../lib/publicLaunch'

/**
 * Warm-paper sign-in page (COTC_SignupConsent R8/D3). Federates to the shared platform via the
 * existing AuthContext; on success App's onboarding gate routes a family-less account back into
 * the wizard, everyone else home. Error copy comes from the platform's errors[0].
 */
export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const returnTo = useReturnDestination()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (busy) return
    setError(null)
    setBusy(true)
    try {
      await login(email.trim(), password)
      navigate(returnTo ?? '/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign you in.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper-page px-6 py-12">
      <div className="w-full max-w-[440px] animate-rise rounded-card border border-line-soft bg-paper-surface p-8 shadow-card">
        <div className="mb-6">
          <Logo insetColor="#fffdf8" />
        </div>
        <h1 className="font-serif text-[26px] font-medium tracking-[-.015em] text-ink">Welcome back.</h1>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">Sign in to your Circle of Trust account.</p>

        <form className="mt-6 grid gap-4" onSubmit={onSubmit}>
          <Input label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          <Input label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" />

          {error && (
            <div role="alert" className="rounded-input bg-honey-bg px-4 py-3 text-[12.5px] leading-relaxed text-honey-fg">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="rounded-btn bg-ink px-[18px] py-3 text-[13.5px] font-medium text-paper-panel hover:bg-ink/90 disabled:opacity-70"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <Link to={withReturnPath('/forgot-password', returnTo)} className="mt-4 inline-block text-[12.5px] text-terra-deep">Forgot password?</Link>
        <Link to={withReturnPath('/confirm-email', returnTo)} className="ml-4 mt-4 inline-block text-[12.5px] text-terra-deep">Confirm email</Link>

        {isPublicOnboardingEnabled() ? <p className="mt-6 text-[12.5px] text-ink-muted">
          New here?{' '}
          <Link to={withReturnPath('/signup', returnTo)} className="text-terra-deep hover:text-terra-hover">
            Create an account
          </Link>
          .
        </p> : <p className="mt-6 text-[12.5px] text-ink-muted">New account registration is paused.</p>}
      </div>
    </div>
  )
}
